import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMount, useUnmount } from 'react-use';
import { List } from 'immutable';
import Script from 'react-load-script';

import CircularProgress from '@material-ui/core/CircularProgress';
import DialogContent from '@material-ui/core/DialogContent';
import { makeStyles } from '@material-ui/core/styles';

import { mkAccountAggregator } from 'data/accounts/types';
import { mkCredential, mkInstitutionLogin} from 'data/institutionLogins/types';

import { getFeatureFlag } from 'utils/featureFlags/selectors';
import { getLogger } from 'utils/logger';
import tracker from 'utils/tracker';
import { Config, ENVIRONMENT, BUILD_TYPE } from 'utils/webappConfig';

import * as actions from '../actions';
import { getInstitutionLogin, getPublicToken } from '../selectors';

import { transformPlaidAccountsToAccounts } from './transformers';

const log = getLogger('PlaidLink/index.js');

/* eslint-disable camelcase */

type Props = {
  trackingProperties: any,

  onClosed: (any) => void,
  onCapOne: (any) => void,
};

const useStyles = makeStyles((_theme) => ({
  plaidIntro: {
    paddingTop: '60px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignContent: 'flex-start',
  },
  addManual: {
    paddingTop: '60px',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  progress: {
    maxWidth: '50%',
  },
  root: {
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    margin: '24px',
  },
  statusContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const PlaidLink = (props: Props) => {
  const { onClosed, onCapOne, trackingProperties } = props;
  const classes = useStyles();
  const dispatch = useDispatch();

  const LinkHandler = useRef();

  const [lastSearchTerm, setLastSearchTerm] = useState(null);
  const [openPlaidLink, setOpenPlaidLink] = useState(false);
  const [, setLinkLoaded] = useState(false);
  const [linkScriptLoaded, setLinkScriptLoaded] = useState(false);

  const [plaidLastError, setPlaidLastError] = useState(null);
  const [plaidRequestId, setPlaidRequestId] = useState(null);

  const institutionLogin = useSelector(getInstitutionLogin);
  const plaidEnvOverride = useSelector((state) => getFeatureFlag(state, 'plaidEnv'));
  const publicToken = useSelector(getPublicToken);

  useMount(() => {
    if (institutionLogin) {
      dispatch(actions.getPlaidPublicToken(institutionLogin));
    } else {
      // can immediately open plaid-link (nothing to wait for)
      setOpenPlaidLink(true);
    }
  });

  useUnmount( () => {
    // make sure global link handler is reset
    LinkHandler.current = null;
  });

  useEffect(() => {
    if (publicToken && !openPlaidLink) {
      setOpenPlaidLink(true);
    }
  }, [openPlaidLink, publicToken, setOpenPlaidLink]);

  useEffect(() => {
    if (linkScriptLoaded && openPlaidLink && !LinkHandler.current) {

      const getPlaidEnv = () => {
        if (institutionLogin) {
          const { channel } = institutionLogin.aggregators.get(0);
          switch (channel) {
            case 'PLAID':
              return 'production';
            case 'PLAID_DEV':
              return 'development';
            case 'PLAID_SANDBOX':
              return 'sandbox';
            default:
              log.error(`Unrecognized ContentProvider Channel for plaid, channel=${channel}`);
              return 'production';
          }
        }
        return plaidEnvOverride || Config.builds[BUILD_TYPE].plaid_env;
      };
      const options = {
        clientName: Config.common.plaid_client_name,
        product: Config.common.plaid_product,
        key: Config.common.plaid_public_key,
        env: getPlaidEnv(),
        token: publicToken,
        webhook: Config.environments[ENVIRONMENT].plaid_webhook,
        countryCodes: ['US'],

        onSuccess: handleSuccess,
        onExit: handleExit,
        onEvent: handleEvent,
        onLoad: () => handleLoad,
      };
      log.info('Creeating Plaid Link Handler', options);

      LinkHandler.current = window.Plaid.create(options);
      LinkHandler.current.open();
    }
  });

  const getChannelFromPlaidEnv = () => {
    if (institutionLogin) {
      return institutionLogin.aggregators.get(0).channel;
    }

    const env = plaidEnvOverride || Config.builds[BUILD_TYPE].plaid_env;
    switch (env) {
      case 'production':
        return 'PLAID';
      case 'development':
        return 'PLAID_DEV';
      case 'sandbox':
        return 'PLAID_SANDBOX';
      default:
        // TODO: Should throw error here!
        return null;
    }
  };

  const handleEvent = (event, metadata) => {
    log.info('Plaid Event', event, metadata, LinkHandler.current);
    if (event === 'TRANSITION_VIEW') {
      if (metadata.view_name === 'CONNECTED') {
        // linkHandler.exit();
      }
    }
    switch (event) {
      case 'ERROR':
        handleEventError(metadata);
        break;
      case 'EXIT':
        // handled by the onExit handler
        break;
      case 'HANDOFF':
        handleEventHandoff(metadata);
        break;
      case 'OPEN':
        handleEventOpen(metadata);
        break;
      case 'SEARCH_INSTITUTION':
        // this is fired anytime when searching - usually per keystroke
        setLastSearchTerm(metadata.institution_search_query);
        break;
      case 'SELECT_INSTITUTION':
        handleSelectInstitution(metadata);
        break;
      case 'SUBMIT_CREDENTIALS':
        // handleSubmitCredentials(metadata);
        break;
      case 'SUBMIT_MFA':
        // handleSubmitMfa(metadata);
        break;
      case 'TRANSITION_VIEW':
        handleEventTransitionView(metadata);
        break;
      default:
        break;
    }
  };

  const handleEventError = (metadata) => {
    const { error_code, error_message, institution_id, institution_name } = metadata;
    setPlaidLastError({ error_code, error_message });

    // TODO: Not sure what to assign to agg_status_code, need to handle channel and id
    tracker.track(tracker.events.addFIError, {
      ...trackingProperties,
      institution_id,
      institution_name,
      error_code: 'unknown',
      agg_status_code: error_code,
      agg_status_detail: error_message,
    });
  };

  const handleEventHandoff = (metadata) => {
    const { institution_id, institution_name, link_session_id, request_id } = metadata;

    //  would send this in the onSuccess handler except Plaid doesn't provide the request_id there
    dispatch(actions.logPlaidItemEvent({
      institutionLogin,
      event: {
        name: 'PLAID_SUCCESS',
        institutionId: institution_id,
        institutionName: institution_name,
        sessionId: link_session_id,
        requestId: request_id,
      },
    }));
  };

  const handleEventOpen = (metadata) => {
    const { link_session_id } = metadata;
    dispatch(actions.logPlaidItemEvent({
      institutionLogin,
      event: {
        name: 'PLAID_OPEN',
        sessionId: link_session_id,
      },
    }));
  };

  const handleEventTransitionView = (metadata) => {
    const { institution_id, institution_name, link_session_id, request_id, view_name } = metadata;
    if (request_id) {
      setPlaidRequestId(request_id);
    }

    if (view_name === 'ERROR') {
      // this should be sent during error event, but Plaid didn't give us the request ID so sending here
      dispatch(actions.logPlaidItemEvent({
        institutionLogin,
        event: {
          name: 'PLAID_ERROR',
          ...(plaidLastError && {
            errorCode: plaidLastError.error_code,
            errorMessage: plaidLastError.error_message,
          }),
          ...(institution_id && {
            institutionId: institution_id,
            institutionName: institution_name,
          }),
          requestId: request_id,
          sessionId: link_session_id,
        },
      }));
    }

  };

  const handleExit = (error, metadata) => {
    log.info('Plaid Link Exit', error, metadata);
    const { institution, link_session_id, request_id, status } = metadata;

    // handle the case when selects Capital One
    if ((institution?.name === 'Capital One') && onCapOne) {
      onCapOne();
      return;
    }

    dispatch(actions.logPlaidItemEvent({
      institutionLogin,
      event: {
        name: 'PLAID_EXIT',
        ...(error && {
          errorCode: error.error_code,
          errorMessage: error.error_message,
        }),
        exitedAt: status,
        institutionId: institution.institution_id,
        institutionName: institution.name,
        sessionId: link_session_id,
        requestId: request_id || plaidRequestId,
      },
    }));

    if (onClosed) {

      let errorTrackingProperties = {};
      if (error) {
        errorTrackingProperties = {
          error_code: 'unknown',
          agg_status_code: error.error_code,
          agg_status_detail: error.error_message,
        };
      }

      // TODO: Seems to be Plaid bug that if close dialog is clicked on search, they return requires_credentials

      let statusTrackingProperties = null;
      switch (status) {
        case 'institution_not_found':
          statusTrackingProperties = {
            step: 'select fi',
            last_search_term: lastSearchTerm,
          };
          break;
        case 'requires_code':
        case 'requires_device':
        case 'requires_questions':
        case 'requires_selections':
          statusTrackingProperties = {
            step: 'mfa',
          };
          break;
        case 'requires_credentials':
          statusTrackingProperties = {
            step: 'credentials',
          };
          break;
        default:
          statusTrackingProperties = {};
      }
      onClosed({
        trackingProperties: {
          ...statusTrackingProperties,
          ...errorTrackingProperties,
        },
      });
    }
  };

  const handleLoad = () => {
    setLinkLoaded(true);
  };

  const handleSelectInstitution = (metadata) => {
    const { institution_id, institution_name } = metadata;

    // exit flow on Cap One select
    if (institution_name === 'Capital One') { // or (institution_id === 'ins_9')
      log.info('Cap One selected - change to FDS');
      LinkHandler.current?.exit({ force: true });

      // return before tracking, will track addFISelect in Capital One Flow
      return;
    }

    tracker.track(tracker.events.addFISelect, {
      ...trackingProperties,
      institution_id,
      institution_name,
      last_search_term: lastSearchTerm,
    });
  };

  // const handleSubmitCredentials = (metadata) => {
  //   const { institution_id, institution_name } = metadata;  // eslint-disable-line camelcase
  //
  // };

  // const handleSubmitMfa = (metadata) => {
  //   const { institution_id, institution_name } = metadata;  // eslint-disable-line camelcase
  // };

  const handleSuccess = (token, metadata) => {
    const { accounts: plaidAccounts, institution, link_session_id } = metadata; // eslint-disable-line camelcase
    log.info('Plaid Link Success', token, link_session_id, institution, plaidAccounts, metadata);

    if (institutionLogin) {
      // we are in update-mode, not sure what is coming back here!
      dispatch(actions.accountDiscoveryPlaidItemUpdateSuccess({ institutionLogin, accountDiscoveryData: null }));
      return;
    }

    const newInstitutionLogin = mkInstitutionLogin({
      cpSetupMode: 'ADD_ACCOUNTS_ONLY',
      name: institution.name,
      institutionId: null,
      cpInstitutionId: institution.institution_id,
      credentials: List([ mkCredential({ key: 'public-token', value: token }) ]),
      aggregators: List([ mkAccountAggregator({ channel: getChannelFromPlaidEnv() }) ]),
    });

    const accounts = transformPlaidAccountsToAccounts(newInstitutionLogin, plaidAccounts);
    dispatch(actions.discoverInstitutionLoginWithAccounts({ institutionLogin: newInstitutionLogin, accounts }));
  };

  const handleScriptLoaded = () => {
    setLinkScriptLoaded(true);
  };

  function onScriptError() {
    log.error('There was an issue loading the link-initialize.js script');
  }

  return (
    <>
      <DialogContent className={classes.root}>
        <div className={classes.statusContainer} >
          <CircularProgress size={100} className={classes.progress} />
        </div>
      </DialogContent>
      <Script
        url="https://cdn.plaid.com/link/v2/stable/link-initialize.js"
        onError={onScriptError}
        onLoad={handleScriptLoaded}
      />
    </>
  );
};

/* eslint-enable camelcase */

export default PlaidLink;
